import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CallsTable from "../components/dashboard/CallsTable";
import StatsGrid from "../components/dashboard/StatsGrid";
import RetryBlock from "../components/common/RetryBlock";
import TranscriptModal from "../components/modals/TranscriptModal";
import LeadUploadModal from "../components/modals/LeadUploadModal";
import useDebounce from "../hooks/useDebounce";
import useModalState from "../hooks/useModalState";
import useShellContext from "../layout/useShellContext";
import { getDashboardCalls, getDashboardStats } from "../services/dashboardApi";
import { getLeadContextStatus, uploadLeadContext } from "../services/leadsApi";
import { formatNow } from "../utils/formatters";
import { PAGE_SIZE } from "../utils/constants";
import { callKey, getStatCards } from "../utils/helpers";
import { normalizeApiError } from "../utils/errors";
import PerformanceCard from "../components/dashboard/PerformanceCard";
import CallSummaryCards from "../components/dashboard/CallSummaryCards";
import CallingAgentSummaryGrid from "../components/calls/CallingAgentSummaryGrid";
import CallingAgentWorkTable from "../components/calls/CallingAgentWorkTable";
import QueueDetailsModal from "../components/modals/QueueDetailsModal";
import UpdateCallStatusModal from "../components/calls/UpdateCallStatusModal";
import { useAuth } from "../context/AuthContext";
import { getCallDetail, getCallingAgentSummary, updateCallWorkflowStatus } from "../services/callsApi";
import { downloadTranscript } from "../utils/callDisplay";
import { isCallingAgentUser } from "../utils/roleAccess";
import {
  getCallHistoryRequestKey,
  groupCallsByPhoneNumber,
  hydrateCallHistoryGroup,
} from "../utils/callHistory";

function TotalCallsIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  );
}

function BookingIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect width="18" height="18" x="3" y="4" rx="2" ry="2" />
      <line x1="16" x2="16" y1="2" y2="6" />
      <line x1="8" x2="8" y1="2" y2="6" />
      <line x1="3" x2="21" y1="10" y2="10" />
      <path d="m9 16 2 2 4-4" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

function TrendIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
      <polyline points="16 7 22 7 22 13" />
    </svg>
  );
}

function decorateStatCards(stats) {
  if (stats?.dashboard_scope === "calling_agent") {
    return [
      {
        key: "my-assigned-calls",
        label: "My Assigned Calls",
        value: Number(stats.my_assigned_calls || 0),
        sub: "Calls assigned to you",
        tone: "gold",
        icon: <TotalCallsIcon />,
      },
      {
        key: "pending-follow-ups",
        label: "Pending Follow-ups",
        value: Number(stats.pending_follow_ups || 0),
        sub: "Active follow-up queue",
        tone: "blue",
        icon: <ClockIcon />,
      },
      {
        key: "completed-follow-ups",
        label: "Completed Follow-ups",
        value: Number(stats.completed_follow_ups || 0),
        sub: "Handled successfully",
        tone: "green",
        icon: <BookingIcon />,
      },
      {
        key: "callback-required",
        label: "Callback Required",
        value: Number(stats.callback_required || 0),
        sub: "Waiting for another touch",
        tone: "amber",
        icon: <TrendIcon />,
      },
    ];
  }

  const icons = {
    total: <TotalCallsIcon />,
    bookings: <BookingIcon />,
    duration: <ClockIcon />,
    rate: <TrendIcon />,
    inbound: <TotalCallsIcon />,
    outbound: <TotalCallsIcon />,
    dropped: <TotalCallsIcon />,
  };

  return getStatCards(stats).map((card) => ({
    ...card,
    icon: icons[card.key],
  }));
}

const initialDashboardData = {
  stats: null,
  leadContextStatus: null,
  calls: [],
  total: 0,
};
const initialCallingAgentSummary = {
  counts: {
    pending_calls: 0,
    assigned_calls: 0,
    completed_followups: 0,
    callback_required: 0,
    today_completed: 0,
  },
  pending_calls: [],
  assigned_calls: [],
  completed_followups: [],
};
const DASHBOARD_GROUPED_CALL_FETCH_LIMIT = 1000;

export default function DashboardPage() {
  const {
    showToast,
    registerPageControls,
    resetPageControls,
    openOutboundModal,
    runBulkCalls,
  } = useShellContext();
  const { user } = useAuth();
  const isCallingAgent = isCallingAgentUser(user);
  const transcriptModal = useModalState(null);
  const leadUploadModal = useModalState(null);
  const [dashboardData, setDashboardData] = useState(initialDashboardData);
  const [callingAgentSummary, setCallingAgentSummary] = useState(
    initialCallingAgentSummary,
  );
  const [, setSummaryLoading] = useState(true);
  const [callsLoading, setCallsLoading] = useState(true);
  const [summaryError, setSummaryError] = useState(null);
  const [callsError, setCallsError] = useState(null);
  const [callingAgentError, setCallingAgentError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState("--");
  const [shellRefreshing, setShellRefreshing] = useState(false);
  const [callSearchInput, setCallSearchInput] = useState("");
  const [callStatusFilter, setCallStatusFilter] = useState("");
  const [callsPage, setCallsPage] = useState(0);
  const [selectedCallKeys, setSelectedCallKeys] = useState(new Set());
  const [leadUploadFile, setLeadUploadFile] = useState(null);
  const [leadUploadActivate, setLeadUploadActivate] = useState(false);
  const [leadContextBusy, setLeadContextBusy] = useState(false);
  const [selectedAssignedCall, setSelectedAssignedCall] = useState(null);
  const [statusModalCall, setStatusModalCall] = useState(null);
  const [statusBusy, setStatusBusy] = useState(false);
  const debouncedSearch = useDebounce(callSearchInput, 400);

  const mountedRef = useRef(true);
  const summaryRequestRef = useRef({ id: 0, controller: null });
  const callsRequestRef = useRef({ id: 0, controller: null });
  const didHandleQueryChangeRef = useRef(false);
  const leadSyncTimeoutRef = useRef(null);
  const dashboardQueryRef = useRef({
    search: "",
    status: "",
  });

  dashboardQueryRef.current = {
    search: debouncedSearch,
    status: callStatusFilter,
  };

  const abortSummaryRequest = useCallback(() => {
    if (summaryRequestRef.current.controller) {
      summaryRequestRef.current.controller.abort();
      summaryRequestRef.current.controller = null;
    }
  }, []);

  const abortCallsRequest = useCallback(() => {
    if (callsRequestRef.current.controller) {
      callsRequestRef.current.controller.abort();
      callsRequestRef.current.controller = null;
    }
  }, []);

  const abortAllRequests = useCallback(() => {
    abortSummaryRequest();
    abortCallsRequest();
  }, [abortCallsRequest, abortSummaryRequest]);

  const loadDashboardData = useCallback(
    async ({
      includeSummary = true,
      includeCalls = true,
      queryOverride,
      trackRefresh = false,
    } = {}) => {
      if (isCallingAgent) {
        return undefined;
      }

      if (!includeSummary && !includeCalls) {
        return undefined;
      }

      const query = queryOverride || dashboardQueryRef.current;
      let firstError = null;
      let hadSuccess = false;

      if (includeSummary) {
        abortSummaryRequest();
        setSummaryLoading(true);
        setSummaryError(null);
      }

      if (includeCalls) {
        abortCallsRequest();
        setCallsLoading(true);
        setCallsError(null);
      }

      if (trackRefresh) {
        setShellRefreshing(true);
      }

      try {
        const tasks = [];

        if (includeSummary) {
          const controller = new AbortController();
          const requestId = summaryRequestRef.current.id + 1;
          summaryRequestRef.current = {
            id: requestId,
            controller,
          };

          tasks.push(
            Promise.all([
              getDashboardStats({ signal: controller.signal }),
              getLeadContextStatus({ signal: controller.signal }),
            ])
              .then(([stats, leadContextStatus]) => {
                if (
                  !mountedRef.current ||
                  controller.signal.aborted ||
                  summaryRequestRef.current.id !== requestId
                ) {
                  return;
                }

                hadSuccess = true;
                setDashboardData((current) => ({
                  ...current,
                  stats,
                  leadContextStatus,
                }));
                setSummaryError(null);
              })
              .catch((error) => {
                const normalizedError = normalizeApiError(error);
                if (
                  !normalizedError.isAbort &&
                  mountedRef.current &&
                  summaryRequestRef.current.id === requestId
                ) {
                  setSummaryError(normalizedError);
                  firstError = firstError || normalizedError;
                }
                return normalizedError;
              })
              .finally(() => {
                if (
                  mountedRef.current &&
                  summaryRequestRef.current.id === requestId
                ) {
                  setSummaryLoading(false);
                  summaryRequestRef.current.controller = null;
                }
              }),
          );
        }

        if (includeCalls) {
          const controller = new AbortController();
          const requestId = callsRequestRef.current.id + 1;
          callsRequestRef.current = {
            id: requestId,
            controller,
          };

          tasks.push(
            getDashboardCalls(
              {
                limit: DASHBOARD_GROUPED_CALL_FETCH_LIMIT,
                offset: 0,
                search: query.search,
                status: query.status,
              },
              { signal: controller.signal },
            )
              .then((response) => {
                if (
                  !mountedRef.current ||
                  controller.signal.aborted ||
                  callsRequestRef.current.id !== requestId
                ) {
                  return;
                }

                const rawCalls = Array.isArray(response)
                  ? response
                  : response?.calls || [];
                hadSuccess = true;
                setDashboardData((current) => ({
                  ...current,
                  calls: rawCalls.map((call, index) => ({
                    ...call,
                    __key: callKey(call, index),
                  })),
                  total: response?.total || rawCalls.length,
                }));
                setCallsError(null);
              })
              .catch((error) => {
                const normalizedError = normalizeApiError(error);
                if (
                  !normalizedError.isAbort &&
                  mountedRef.current &&
                  callsRequestRef.current.id === requestId
                ) {
                  setCallsError(normalizedError);
                  firstError = firstError || normalizedError;
                }
                return normalizedError;
              })
              .finally(() => {
                if (
                  mountedRef.current &&
                  callsRequestRef.current.id === requestId
                ) {
                  setCallsLoading(false);
                  callsRequestRef.current.controller = null;
                }
              }),
          );
        }

        await Promise.allSettled(tasks);

        if (mountedRef.current && hadSuccess) {
          setLastUpdated(formatNow());
        }

        if (firstError) {
          throw firstError;
        }

        return undefined;
      } finally {
        if (trackRefresh && mountedRef.current) {
          setShellRefreshing(false);
        }
      }
    },
    [abortCallsRequest, abortSummaryRequest, isCallingAgent],
  );

  const loadCallingAgentDashboard = useCallback(
    async ({ trackRefresh = false } = {}) => {
      if (!isCallingAgent) {
        return undefined;
      }

      if (trackRefresh && mountedRef.current) {
        setShellRefreshing(true);
      }

      setCallsLoading(true);
      setCallingAgentError(null);

      try {
        const summary = await getCallingAgentSummary();

        if (!mountedRef.current) {
          return undefined;
        }

        setCallingAgentSummary({
          ...initialCallingAgentSummary,
          ...summary,
          counts: {
            ...initialCallingAgentSummary.counts,
            ...(summary?.counts || {}),
          },
        });
        setLastUpdated(formatNow());
        return undefined;
      } catch (requestError) {
        const normalizedError = normalizeApiError(requestError);

        if (!normalizedError.isAbort && mountedRef.current) {
          setCallingAgentError(normalizedError);
        }

        throw normalizedError;
      } finally {
        if (mountedRef.current) {
          setCallsLoading(false);
        }

        if (trackRefresh && mountedRef.current) {
          setShellRefreshing(false);
        }
      }
    },
    [isCallingAgent],
  );

  const handleRefresh = useCallback(
    () =>
      isCallingAgent
        ? loadCallingAgentDashboard({ trackRefresh: true })
        : loadDashboardData({
            includeSummary: true,
            includeCalls: true,
            trackRefresh: true,
          }),
    [isCallingAgent, loadCallingAgentDashboard, loadDashboardData],
  );

  useEffect(() => {
    mountedRef.current = true;

    if (isCallingAgent) {
      loadCallingAgentDashboard().catch(() => undefined);
    } else {
      loadDashboardData({
        includeSummary: true,
        includeCalls: true,
      }).catch(() => undefined);
    }

    return () => {
      mountedRef.current = false;
      abortAllRequests();

      if (leadSyncTimeoutRef.current) {
        window.clearTimeout(leadSyncTimeoutRef.current);
        leadSyncTimeoutRef.current = null;
      }
    };
  }, [abortAllRequests, isCallingAgent, loadCallingAgentDashboard, loadDashboardData]);

  useEffect(() => {
    if (isCallingAgent) {
      return;
    }

    if (!didHandleQueryChangeRef.current) {
      didHandleQueryChangeRef.current = true;
      return;
    }

    loadDashboardData({
      includeSummary: false,
      includeCalls: true,
      queryOverride: {
        search: debouncedSearch,
        status: callStatusFilter,
      },
    }).catch(() => undefined);
  }, [callStatusFilter, debouncedSearch, isCallingAgent, loadDashboardData]);

  useEffect(() => {
    registerPageControls({
      onRefresh: handleRefresh,
      refreshEvery: "Manual",
      lastUpdated,
      isRefreshing: shellRefreshing,
    });

    return () => {
      resetPageControls();
    };
  }, [
    handleRefresh,
    lastUpdated,
    registerPageControls,
    resetPageControls,
    shellRefreshing,
  ]);

  const cards = useMemo(
    () => decorateStatCards(dashboardData.stats || {}),
    [dashboardData.stats],
  );
  const primaryStats = cards.slice(0, 4);
  const secondaryStats = cards.slice(4);
  const groupedCalls = useMemo(
    () => groupCallsByPhoneNumber(dashboardData.calls || []),
    [dashboardData.calls],
  );
  const callsTotal = groupedCalls.length;
  const calls = useMemo(() => {
    const startIndex = callsPage * PAGE_SIZE;
    return groupedCalls.slice(startIndex, startIndex + PAGE_SIZE);
  }, [callsPage, groupedCalls]);
  const performanceData = dashboardData.stats?.performance || {};

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(callsTotal / PAGE_SIZE) - 1);
    setCallsPage((current) => (current > maxPage ? maxPage : current));
  }, [callsTotal]);

  const handleOpenCallDetails = useCallback(
    async (call) => {
      const modalRequestKey = getCallHistoryRequestKey(call);

      transcriptModal.open({
        ...call,
        __modalRequestKey: modalRequestKey,
      });

      const detailedGroup = await hydrateCallHistoryGroup(call, getCallDetail);

      if (!detailedGroup) {
        return;
      }

      transcriptModal.update((currentCall) => {
        const currentKey =
          currentCall?.__modalRequestKey ||
          currentCall?.phoneGroupKey ||
          currentCall?.__groupKey ||
          currentCall?.call_id ||
          currentCall?.id;

        if (currentKey !== modalRequestKey) {
          return currentCall;
        }

        return {
          ...detailedGroup,
          __modalRequestKey: modalRequestKey,
        };
      });
    },
    [transcriptModal],
  );

  const handleOpenAssignedCall = useCallback((call) => {
    setSelectedAssignedCall(call);
  }, []);

  const handleDirectAssignedCall = useCallback(
    (call) => {
      openOutboundModal({
        phone: call.phone || call.phone_number || "",
        name: call.patient_name || call.caller_name || "",
        reason: call.service_type || call.reason || "follow-up",
        summary: call.summary || "",
      });
    },
    [openOutboundModal],
  );

  const callingAgentCounts = callingAgentSummary?.counts || initialCallingAgentSummary.counts;

  if (isCallingAgent) {
    return (
      <>
        <div className="section-title">Performance Overview</div>
        <CallingAgentSummaryGrid counts={callingAgentCounts} />

        {callingAgentError ? (
          <RetryBlock
            message="Assigned follow-up data is unavailable right now."
            onRetry={() => loadCallingAgentDashboard({ trackRefresh: true }).catch(() => undefined)}
          />
        ) : null}

        <CallingAgentWorkTable
          title="Pending Calls"
          items={callingAgentSummary.pending_calls || []}
          loading={callsLoading}
          emptyTitle="No pending calls found"
          emptySubtitle="Assigned follow-up work waiting on you will appear here."
          onView={handleOpenAssignedCall}
          onDirectCall={handleDirectAssignedCall}
          onUpdateStatus={setStatusModalCall}
        />

        <CallingAgentWorkTable
          title="Assigned Calls"
          items={callingAgentSummary.assigned_calls || []}
          loading={callsLoading}
          emptyTitle="No assigned calls found"
          emptySubtitle="Active work in progress will appear here."
          onView={handleOpenAssignedCall}
          onDirectCall={handleDirectAssignedCall}
          onUpdateStatus={setStatusModalCall}
        />

        <CallingAgentWorkTable
          title="Completed Follow-ups"
          items={callingAgentSummary.completed_followups || []}
          loading={callsLoading}
          emptyTitle="No completed follow-ups yet"
          emptySubtitle="Completed follow-up work will appear here after status updates."
          onView={handleOpenAssignedCall}
          onDirectCall={handleDirectAssignedCall}
          onUpdateStatus={setStatusModalCall}
        />

        <QueueDetailsModal
          open={Boolean(selectedAssignedCall)}
          queueCall={selectedAssignedCall}
          onClose={() => setSelectedAssignedCall(null)}
        />

        <UpdateCallStatusModal
          open={Boolean(statusModalCall)}
          busy={statusBusy}
          queueCall={statusModalCall}
          onClose={() => {
            if (!statusBusy) {
              setStatusModalCall(null);
            }
          }}
          onSubmit={async (payload) => {
            if (!statusModalCall?.call_id) {
              return;
            }

            setStatusBusy(true);

            try {
              await updateCallWorkflowStatus(statusModalCall.call_id, payload);
              showToast("Call status updated successfully.", false, 4000);
              setStatusModalCall(null);
              await loadCallingAgentDashboard({ trackRefresh: true });
            } catch (requestError) {
              const normalizedError = normalizeApiError(requestError);

              if (!normalizedError.isAbort) {
                showToast(normalizedError.message, true, 5000);
              }
            } finally {
              setStatusBusy(false);
            }
          }}
        />
      </>
    );
  }

  return (
    <>
      <div className="section-title">Performance Overview</div>
      <StatsGrid cards={primaryStats} />
      <div className="calls-performance-row">
        <CallSummaryCards cards={secondaryStats} />
        <PerformanceCard
          chartData={performanceData.chartData}
          metrics={performanceData.metrics}
        />
      </div>

      {summaryError && dashboardData.stats ? (
        <RetryBlock
          message="Stats are unavailable right now. Showing the latest loaded values."
          onRetry={() =>
            loadDashboardData({
              includeSummary: true,
              includeCalls: false,
            }).catch(() => undefined)
          }
        />
      ) : null}

      {callsError && calls.length ? (
        <RetryBlock
          message="Recent call data is unavailable right now."
          onRetry={() =>
            loadDashboardData({
              includeSummary: false,
              includeCalls: true,
            }).catch(() => undefined)
          }
        />
      ) : null}

      <CallsTable
        calls={calls}
        total={callsTotal}
        currentPage={callsPage}
        searchValue={callSearchInput}
        onSearchChange={(value) => {
          setCallSearchInput(value);
          setCallsPage(0);
        }}
        statusFilter={callStatusFilter}
        onStatusFilterChange={(value) => {
          setCallStatusFilter(value);
          setCallsPage(0);
        }}
        selectedIds={selectedCallKeys}
        onToggleId={(id, checked) =>
          setSelectedCallKeys((current) => {
            const next = new Set(current);

            if (checked) {
              next.add(id);
            } else {
              next.delete(id);
            }

            return next;
          })
        }
        onToggleAll={(checked) =>
          setSelectedCallKeys(
            checked
              ? new Set(
                  calls
                    .filter((call) => call.phone_number)
                    .map((call) => call.__groupKey || call.__key),
                )
              : new Set(),
          )
        }
        onPrevPage={() => setCallsPage((current) => Math.max(0, current - 1))}
        onNextPage={() => setCallsPage((current) => current + 1)}
        onOpenTranscript={handleOpenCallDetails}
        onDownloadTranscript={(call) => {
          if (!downloadTranscript(call)) {
            showToast("No transcript available", true, 4000);
          }
        }}
        loading={callsLoading}
      />

      {selectedCallKeys.size > 0 ? (
        <div className="inline-selection-bar">
          <span>{selectedCallKeys.size} selected</span>
          <div className="page-actions">
            <button
              type="button"
              className="btn compact-btn"
              onClick={() => setSelectedCallKeys(new Set())}
            >
              Clear Selection
            </button>
            <button
              type="button"
              className="btn btn-gold compact-btn"
              onClick={async () => {
                const targets = calls
                  .filter(
                    (call) =>
                      selectedCallKeys.has(call.__groupKey || call.__key) &&
                      call.phone_number,
                  )
                  .map((call) => ({
                    phone: call.phone_number,
                    name: call.caller_name || "Patient",
                    reason: "follow-up",
                  }));

                const result = await runBulkCalls(targets);

                if (!result.cancelled) {
                  setSelectedCallKeys(new Set());
                  loadDashboardData({
                    includeSummary: true,
                    includeCalls: true,
                  }).catch(() => undefined);
                }
              }}
            >
              Call Selected
            </button>
          </div>
        </div>
      ) : null}

      <TranscriptModal
        open={transcriptModal.isOpen}
        call={transcriptModal.data}
        showCallMetadata
        onDownloadTranscript={(call) => {
          if (!downloadTranscript(call)) {
            showToast("No transcript available", true, 4000);
          }
        }}
        onClose={transcriptModal.close}
        onCallAgain={(phone, name) => {
          transcriptModal.close();
          openOutboundModal({
            phone,
            name,
            reason: "follow-up",
            summary: "",
          });
        }}
      />

      <LeadUploadModal
        open={leadUploadModal.isOpen}
        file={leadUploadFile}
        activate={leadUploadActivate}
        busy={leadContextBusy}
        onFileChange={setLeadUploadFile}
        onActivateChange={setLeadUploadActivate}
        onClose={() => {
          if (!leadContextBusy) {
            leadUploadModal.close();
            setLeadUploadFile(null);
            setLeadUploadActivate(false);
          }
        }}
        onSubmit={async (event) => {
          event.preventDefault();

          if (!leadUploadFile) {
            showToast("Choose a file first.", true);
            return;
          }

          const formData = new FormData();
          formData.append("file", leadUploadFile);
          formData.append("activate", leadUploadActivate ? "true" : "false");
          setLeadContextBusy(true);

          try {
            const response = await uploadLeadContext(formData);
            setDashboardData((current) => ({
              ...current,
              leadContextStatus: response?.status || current.leadContextStatus,
            }));
            showToast(
              `${response?.lead_count || 0} leads loaded.`,
              false,
              5000,
            );
            leadUploadModal.close();
            setLeadUploadFile(null);
            setLeadUploadActivate(false);
          } catch (requestError) {
            const normalizedError = normalizeApiError(requestError);
            if (!normalizedError.isAbort) {
              showToast(normalizedError.message, true, 7000);
            }
          } finally {
            setLeadContextBusy(false);
          }
        }}
      />
    </>
  );
}
